// TODO: stub file. Replace with real backend wiring once endpoints are spec'd.

import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { companyPayrollRunsApi } from "./payrollRunsApi";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type { IPayrollPolicy } from "./payrollPoliciesApi";
import type {
  IWpsConfig,
  IPaymentMethodConfig,
  ISifBank,
} from "./countryRulesApi";
import type {
  LopSourceEnum,
  PenaltyAmountTypeEnum,
} from "@/utils/enums/payroll.enum";

export type { IWpsConfig, IPaymentMethodConfig };

/**
 * Everything that docks pay for time not worked. Replaces the LOP half of the
 * old `attendanceSettings` plus the whole of `leaveSettings`, which were split
 * by data source rather than by what they do — so "Loss of Pay" was a card on
 * two different tabs.
 */
export type ILopConfig = {
  lopSource: LopSourceEnum;
  /**
   * Bounds the OVERTIME calculation as well as LOP — it is the cycle's
   * attendance boundary. Edited here because this section owns it.
   */
  attendanceCutoffDay: number | null;
  includeUnapprovedLeaveAsLop: boolean;
  leavesDuringNoticePaid: boolean;
};

/** What overtime adds. The other half of the old `attendanceSettings`. */
export type IOvertimeConfig = {
  enabled: boolean;
  capHoursPerMonth: number | null;
  multiplierWeekday: number;
  multiplierWeekend: number;
  multiplierHoliday: number;
};

export type IPenaltyRuleConfig = {
  enabled: boolean;
  gracePeriodMinutes: number;
  amountType: PenaltyAmountTypeEnum;
  amountValue: number;
  freeOccurrencesPerMonth: number;
  maxOccurrencesPerMonth: number | null;
  maxAmountPerMonth: number | null;
};

export type IPenaltyConfig = {
  attendance: {
    enabled: boolean;
    lateArrival: IPenaltyRuleConfig;
    earlyCheckout: IPenaltyRuleConfig;
  };
};

export type IBranchAddressCountry = {
  id: number;
  name: string;
  nationality: string;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
};

export type IBranchAddress = {
  id: number;
  userId: number | null;
  companyId: number | null;
  branchId: number;
  street: string;
  countryId: number;
  state: string;
  city: string;
  zipCode: string;
  createdAt: string;
  updatedAt: string;
  country: IBranchAddressCountry;
};

/**
 * Which run-critical payroll settings a branch has, served on the list payload.
 *
 * Exists so the settings branch list can draw a per-branch readiness chip from
 * one request. Before it, the client fired the per-branch `country-rules`,
 * `payment-method` and `wps` endpoints for every row — 3N requests to render
 * one list.
 *
 * OPTIONAL ON PURPOSE: an API deployed before `feat/branch-payroll-readiness`
 * does not send it, and `useBranchPayrollReadiness` falls back to the
 * per-branch requests when it is absent. Do not make it required until that
 * API change is live everywhere.
 */
/**
 * Copyable sections, mirroring the API's `BranchConfigSectionEnum`.
 *
 * WPS is absent deliberately: `establishmentId` / `employerId` identify one
 * MoHRE establishment, the registered bank is scoped to a country rule the
 * target may not use, and the rest of that block is not consumed by anything.
 * Copying a blanked WPS block also made the target report itself WPS-ready,
 * since readiness keys off the presence of `enabled`.
 */
export type BranchConfigSection =
  | "payrollPolicy"
  | "countryRules"
  | "paymentMethod"
  | "lop"
  | "overtime"
  | "penalties";

/** Mirrors the API's `WpsReadinessIssueEnum`: why a branch cannot file WPS. */
export type WpsReadinessIssue =
  | "NOT_CONFIGURED"
  | "WPS_DISABLED"
  | "ESTABLISHMENT_ID_MISSING"
  | "SIF_BANK_MISSING"
  | "SIF_BANK_INACTIVE";

export type IBranchReadiness = {
  /** An ACTIVE country rule is linked and flagged default — create refuses otherwise. */
  hasCountryRules: boolean;
  /** Linked rules regardless of the default flag — distinguishes the two failures. */
  countryRuleCount: number;
  /** An ACTIVE payroll policy is assigned. */
  hasPayrollPolicy: boolean;
  hasPaymentMethod: boolean;
  /** WPS can file what this branch pays through it (see `wpsRequired`). */
  hasWps: boolean;
  /**
   * The reasons behind the flags above. Optional: an API older than the P4
   * payroll fixes does not send them, and the generic message is used.
   */
  defaultCountryRuleInactive?: boolean;
  payrollPolicyInactive?: boolean;
  /** Pay periods that leave days out (D3). */
  payrollPeriodIssue?: "SAME_MONTH_GAP" | "CROSS_MONTH_GAP" | null;
  /** Items on this branch fall back to WPS, so WPS must be filable. */
  wpsRequired?: boolean;
  wpsIssue?: WpsReadinessIssue | null;
  completedCount: number;
  totalCount: number;
};

export type IBranchConfig = {
  id: number;
  name: string;
  code: string;
  isHeadOffice: boolean;
  phoneNumber: string;
  landline: string;
  poBox: string;
  image: string;
  address: IBranchAddress;
  city: string;
  location: string;
  payrollPolicy: IPayrollPolicy | null;
  readiness?: IBranchReadiness;
};

export type IPayrollGroupDepartment = {
  id: number;
  name: string;
  code?: string;
};

export type IPayrollGroupMemberPreview = {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
};

/** Full member row — only the by-id endpoint returns these. */
export type IPayrollGroupMember = IPayrollGroupMemberPreview & {
  email?: string;
  employeeId?: string | null;
  department?: IPayrollGroupDepartment | null;
  position?: { id: number; title: string } | null;
};

/**
 * The list and the by-id endpoints return DIFFERENT shapes, and the difference
 * matters:
 *
 * - list  → `memberPreview` (max 4) + `memberCount`, and `departments`
 * - by-id → `members` (all, with names) + `memberCount`, and `departments`
 *
 * Neither returns `memberUserIds` or `departmentIds`; derive those from
 * `members` / `departments` when seeding the edit form.
 */
export type IPayrollGroup = {
  id: number;
  name: string;
  isActive: boolean;
  departments?: IPayrollGroupDepartment[];
  /** List only, capped at 4 rows. */
  memberPreview?: IPayrollGroupMemberPreview[];
  /** By-id only — every member, with names for the form's chips. */
  members?: IPayrollGroupMember[];
  memberCount?: number;
};

/**
 * An employee row from `GET /company/payroll/employees`.
 *
 * The previous shape here (`{ id, name, avatar }`) matched no endpoint — the
 * query pointed at a URL that does not exist, so nothing ever surfaced the
 * mismatch. The service returns first/last name separately.
 */
export type IPayrollEmployee = {
  id: number;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  profileImage?: string | null;
  employeeId?: string | number | null;
  department?: { id: number; name: string } | null;
  position?: { id: number; title: string } | null;
};

export type IBranchCountryRule = {
  id: number;
  country: { id: number; name: string; countryCode: string };
  isDefault: boolean;
};

export type IPayrollGroupPayload = {
  name: string;
  memberUserIds: number[];
  departmentIds: number[];
  isActive: boolean;
};

export type IGetPayrollGroupsParams = {
  branchId: number;
  page?: number;
  limit?: number;
  /** Free-text match on group name — applied server-side. */
  search?: string;
};

/**
 * The run wizard's period options are derived from the branch's policy and
 * live in the runs slice, which `invalidatesTags` here cannot reach (R22).
 * Without this, re-pointing a branch's policy left the wizard offering the old
 * policy's periods until a reload.
 */
const invalidateWizardPeriods = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<unknown>;
  },
) => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  dispatch(companyPayrollRunsApi.util.invalidateTags(["payrollPeriodOptions"]));
};

export const companyBranchConfigsApi = createApi({
  reducerPath: "companyBranchConfigsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "branchConfigs",
    "branchCountryRules",
    "payrollGroups",
    "payrollGroup",
    "payrollEmployees",
    "branchSifBanks",
    "branchConfig:wps",
    "branchConfig:paymentMethod",
    "branchConfig:lop",
    "branchConfig:overtime",
    "branchConfig:penalty",
  ],
  endpoints: (builder) => ({
    getBranchConfigs: builder.query<IApiResponse<IBranchConfig[]>, void>({
      query: () => ({
        url: "/company/settings/payroll/branch-configs",
        method: "GET",
      }),
      providesTags: ["branchConfigs"],
    }),
    getBranchCountryRules: builder.query<
      IApiResponse<IBranchCountryRule[]>,
      number
    >({
      query: (branchId) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/country-rules`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "branchCountryRules", id }],
    }),
    updateBranchCountryRules: builder.mutation<
      IApiResponse<IBranchCountryRule[]>,
      { branchId: number; countryRuleIds: number[] }
    >({
      query: ({ branchId, countryRuleIds }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/country-rules`,
        method: "PUT",
        body: { countryRuleIds },
      }),
      invalidatesTags: (_r, _e, { branchId }) => [
        "branchConfigs",
        { type: "branchCountryRules", id: branchId },
        // Changing which countries a branch uses changes which banks it may pick.
        { type: "branchSifBanks", id: branchId },
      ],
    }),
    setDefaultCountryRule: builder.mutation<
      IApiResponse<null>,
      { branchId: number; ruleId: number }
    >({
      query: ({ branchId, ruleId }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/country-rules/${ruleId}/set-default`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, { branchId }) => [
        { type: "branchCountryRules", id: branchId },
        // `readiness.hasCountryRules` keys off the DEFAULT, not the links, so
        // this mutation moves the branch list's readiness chip even though it
        // touches no link. Without this the chip stayed on its stale value
        // until something else happened to refetch the list.
        "branchConfigs",
      ],
    }),
    updateBranchPolicy: builder.mutation<
      IApiResponse<IBranchConfig>,
      { branchId: number; payrollPolicyId: number }
    >({
      query: ({ branchId, payrollPolicyId }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/policy`,
        method: "PUT",
        body: { payrollPolicyId },
      }),
      invalidatesTags: ["branchConfigs"],
      onQueryStarted: invalidateWizardPeriods,
    }),
    getPayrollGroups: builder.query<
      IApiPaginatedResponse<IPayrollGroup[]>,
      IGetPayrollGroupsParams
    >({
      query: ({ branchId, page = 1, limit = 10, search }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/groups`,
        method: "GET",
        // Search server-side: the list is paginated, so filtering a received
        // page would only match the rows that happen to be on it.
        params: { page, limit, ...(search ? { search } : {}) },
      }),
      providesTags: ["payrollGroups"],
    }),
    getPayrollGroupById: builder.query<
      IApiResponse<IPayrollGroup>,
      { branchId: number; id: number }
    >({
      query: ({ branchId, id }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/groups/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, { id }) => [{ type: "payrollGroup", id }],
    }),
    createPayrollGroup: builder.mutation<
      IApiResponse<IPayrollGroup>,
      { branchId: number; data: IPayrollGroupPayload }
    >({
      query: ({ branchId, data }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/groups`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payrollGroups"],
    }),
    updatePayrollGroup: builder.mutation<
      IApiResponse<IPayrollGroup>,
      { branchId: number; id: number; data: IPayrollGroupPayload }
    >({
      query: ({ branchId, id, data }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/groups/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "payrollGroups",
        { type: "payrollGroup", id },
      ],
    }),
    /**
     * Server-side TOGGLE — it flips `isActive` itself and ignores any body, so
     * a double submit would flip twice. Guard the control while in flight.
     */
    togglePayrollGroupStatus: builder.mutation<
      IApiResponse<IPayrollGroup>,
      { branchId: number; id: number }
    >({
      query: ({ branchId, id }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/groups/${id}/status`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "payrollGroups",
        { type: "payrollGroup", id },
      ],
    }),
    deletePayrollGroup: builder.mutation<
      IApiResponse<null>,
      { branchId: number; id: number }
    >({
      query: ({ branchId, id }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/groups/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["payrollGroups"],
    }),
    /**
     * Employees of one branch.
     *
     * The URL was `settings/payroll/branch-configs/:branchId/employees`, which
     * has never existed — every call 404'd. The real route is
     * `GET /company/payroll/employees` with `branchId` as a query param.
     *
     * `limit` is explicit because the endpoint's DTO extends `PaginationDto`,
     * which defaults it to 10 — a picker that silently shows the first ten
     * employees of a branch is worse than one that errors. Pass `search` for
     * larger branches; filtering is the server's job.
     */
    getPayrollEmployees: builder.query<
      IApiPaginatedResponse<IPayrollEmployee[]>,
      { branchId: number; search?: string; page?: number; limit?: number }
    >({
      query: ({ branchId, search, page = 1, limit = 100 }) => ({
        url: `/company/payroll/employees`,
        method: "GET",
        params: { branchId, page, limit, ...(search ? { search } : {}) },
      }),
      providesTags: ["payrollEmployees"],
    }),

    // ─── WPS ─────────────────────────────────────────────────────────────────

    /**
     * The SIF banks this branch may actually select — across ALL its linked
     * country rules, which is the same set the WPS PUT validates against.
     *
     * Replaces per-country-rule fetching in the WPS form, which offered only
     * the first linked rule's banks and so hid valid choices on a
     * multi-country branch. Unpaginated by design: it feeds a picker, and the
     * old call inherited a `limit: 10` default that silently truncated it.
     */
    /**
     * Copy another branch's payroll configuration onto this one.
     *
     * Invalidates every per-section tag for the target: the copy writes the
     * policy, country rules and four JSONB sections in one request, so each
     * open section must refetch or it would keep showing pre-copy values.
     */
    copyBranchConfig: builder.mutation<
      IApiResponse<IBranchConfig>,
      {
        branchId: number;
        sourceBranchId: number;
        sections?: BranchConfigSection[];
      }
    >({
      query: ({ branchId, ...body }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/copy-from`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { branchId }) => [
        "branchConfigs",
        { type: "branchCountryRules", id: branchId },
        { type: "branchSifBanks", id: branchId },
        { type: "branchConfig:wps", id: branchId },
        { type: "branchConfig:paymentMethod", id: branchId },
        { type: "branchConfig:lop", id: branchId },
        { type: "branchConfig:overtime", id: branchId },
        { type: "branchConfig:penalty", id: branchId },
      ],
      onQueryStarted: invalidateWizardPeriods,
    }),

    getBranchSifBanks: builder.query<IApiResponse<ISifBank[]>, number>({
      query: (branchId) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/sif-banks`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "branchSifBanks", id }],
    }),

    getBranchWps: builder.query<IApiResponse<IWpsConfig | null>, number>({
      query: (branchId) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/wps`,
        method: "GET",
      }),
      transformResponse: (
        raw: IApiResponse<
          | { wpsSettings: IWpsConfig | Record<string, never> | null }
          | IWpsConfig
          | null
        >,
      ) => {
        const maybeSettings =
          raw.data && "wpsSettings" in raw.data
            ? raw.data.wpsSettings
            : raw.data;
        const hydrated =
          maybeSettings &&
          typeof maybeSettings === "object" &&
          "enabled" in maybeSettings
            ? (maybeSettings as IWpsConfig)
            : null;
        return { ...raw, data: hydrated };
      },
      providesTags: (_r, _e, id) => [{ type: "branchConfig:wps", id }],
    }),

    updateBranchWps: builder.mutation<
      IApiResponse<IWpsConfig>,
      { branchId: number; data: IWpsConfig }
    >({
      query: ({ branchId, data }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/wps`,
        method: "PUT",
        body: data,
      }),
      // `branchConfigs` too: the list carries `readiness.hasWps` (R22).
      invalidatesTags: (_r, _e, { branchId }) => [
        { type: "branchConfig:wps", id: branchId },
        "branchConfigs",
      ],
    }),

    // ─── Payment Method ───────────────────────────────────────────────────────

    getBranchPaymentMethod: builder.query<
      IApiResponse<IPaymentMethodConfig | null>,
      number
    >({
      query: (branchId) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/payment-method`,
        method: "GET",
      }),
      transformResponse: (
        raw: IApiResponse<
          | {
              paymentMethodSettings:
                IPaymentMethodConfig | Record<string, never> | null;
            }
          | IPaymentMethodConfig
          | null
        >,
      ) => {
        const maybeSettings =
          raw.data && "paymentMethodSettings" in raw.data
            ? raw.data.paymentMethodSettings
            : raw.data;
        const hydrated =
          maybeSettings &&
          typeof maybeSettings === "object" &&
          "defaultMethod" in maybeSettings
            ? (maybeSettings as IPaymentMethodConfig)
            : null;
        return { ...raw, data: hydrated };
      },
      providesTags: (_r, _e, id) => [
        { type: "branchConfig:paymentMethod", id },
      ],
    }),

    updateBranchPaymentMethod: builder.mutation<
      IApiResponse<IPaymentMethodConfig>,
      { branchId: number; data: IPaymentMethodConfig }
    >({
      query: ({ branchId, data }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/payment-method`,
        method: "PUT",
        body: data,
      }),
      // `branchConfigs` too: the list carries `readiness.hasPaymentMethod`
      // and `wpsRequired` (R22).
      invalidatesTags: (_r, _e, { branchId }) => [
        { type: "branchConfig:paymentMethod", id: branchId },
        "branchConfigs",
      ],
    }),

    // ─── Loss of Pay ──────────────────────────────────────────────────────────

    getBranchLop: builder.query<IApiResponse<ILopConfig | null>, number>({
      query: (branchId) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/lop`,
        method: "GET",
      }),
      /**
       * An unconfigured branch reads back `{}`, which must stay distinguishable
       * from a saved config — the section renders defaults for `null` and the
       * saved values otherwise.
       *
       * ANY key means configured, rather than one nominated key. Probing a
       * single field has now failed twice here: the old Leave query tested for
       * `leavePayBasis`, which the type never had, so every branch read back
       * `null` and showed defaults over its saved values; and this section
       * merged in the two ex-`leaveSettings` toggles, so a branch that
       * configured leave but never attendance has no `lopSource` to find. The
       * migration carries keys across without inventing any, which is what
       * keeps "never configured" honest — so emptiness is the only safe test.
       */
      transformResponse: (
        raw: IApiResponse<
          | { lopSettings: ILopConfig | Record<string, never> | null }
          | ILopConfig
          | null
        >,
      ) => {
        const maybeSettings =
          raw.data && "lopSettings" in raw.data
            ? raw.data.lopSettings
            : raw.data;
        const hydrated =
          maybeSettings &&
          typeof maybeSettings === "object" &&
          Object.keys(maybeSettings).length > 0
            ? (maybeSettings as ILopConfig)
            : null;
        return { ...raw, data: hydrated };
      },
      providesTags: (_r, _e, id) => [{ type: "branchConfig:lop", id }],
    }),

    updateBranchLop: builder.mutation<
      IApiResponse<ILopConfig>,
      { branchId: number; data: ILopConfig }
    >({
      query: ({ branchId, data }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/lop`,
        method: "PUT",
        body: data,
      }),
      /**
       * Only this tag: the overtime SECTION payload holds no cutoff, and the
       * overtime screen reads the cutoff through this very query, so it
       * refetches from this tag already.
       */
      invalidatesTags: (_r, _e, { branchId }) => [
        { type: "branchConfig:lop", id: branchId },
      ],
    }),

    // ─── Overtime ─────────────────────────────────────────────────────────────

    getBranchOvertime: builder.query<
      IApiResponse<IOvertimeConfig | null>,
      number
    >({
      query: (branchId) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/overtime`,
        method: "GET",
      }),
      transformResponse: (
        raw: IApiResponse<
          | { overtimeSettings: IOvertimeConfig | Record<string, never> | null }
          | IOvertimeConfig
          | null
        >,
      ) => {
        const maybeSettings =
          raw.data && "overtimeSettings" in raw.data
            ? raw.data.overtimeSettings
            : raw.data;
        // Any key means configured — see the LOP query above for why a single
        // nominated field is not a safe probe.
        const hydrated =
          maybeSettings &&
          typeof maybeSettings === "object" &&
          Object.keys(maybeSettings).length > 0
            ? (maybeSettings as IOvertimeConfig)
            : null;
        return { ...raw, data: hydrated };
      },
      providesTags: (_r, _e, id) => [{ type: "branchConfig:overtime", id }],
    }),

    updateBranchOvertime: builder.mutation<
      IApiResponse<IOvertimeConfig>,
      { branchId: number; data: IOvertimeConfig }
    >({
      query: ({ branchId, data }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/overtime`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { branchId }) => [
        { type: "branchConfig:overtime", id: branchId },
      ],
    }),

    // ─── Penalties ──────────────────────────────────────────────────────────

    getBranchPenalties: builder.query<
      IApiResponse<IPenaltyConfig | null>,
      number
    >({
      query: (branchId) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/penalties`,
        method: "GET",
      }),
      transformResponse: (
        raw: IApiResponse<
          | { penaltySettings: IPenaltyConfig | Record<string, never> | null }
          | IPenaltyConfig
          | null
        >,
      ) => {
        const maybeSettings =
          raw.data && "penaltySettings" in raw.data
            ? raw.data.penaltySettings
            : raw.data;
        const hydrated =
          maybeSettings &&
          typeof maybeSettings === "object" &&
          "attendance" in maybeSettings
            ? (maybeSettings as IPenaltyConfig)
            : null;
        return { ...raw, data: hydrated };
      },
      providesTags: (_r, _e, id) => [{ type: "branchConfig:penalty", id }],
    }),

    updateBranchPenalties: builder.mutation<
      IApiResponse<IPenaltyConfig>,
      { branchId: number; data: IPenaltyConfig }
    >({
      query: ({ branchId, data }) => ({
        url: `/company/settings/payroll/branch-configs/${branchId}/penalties`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { branchId }) => [
        { type: "branchConfig:penalty", id: branchId },
      ],
    }),
  }),
});

export const {
  useGetBranchConfigsQuery,
  useGetBranchCountryRulesQuery,
  useUpdateBranchCountryRulesMutation,
  useSetDefaultCountryRuleMutation,
  useUpdateBranchPolicyMutation,
  useGetPayrollGroupsQuery,
  useGetPayrollGroupByIdQuery,
  useCreatePayrollGroupMutation,
  useUpdatePayrollGroupMutation,
  useTogglePayrollGroupStatusMutation,
  useDeletePayrollGroupMutation,
  useGetPayrollEmployeesQuery,
  useCopyBranchConfigMutation,
  useGetBranchSifBanksQuery,
  useGetBranchWpsQuery,
  useUpdateBranchWpsMutation,
  useGetBranchPaymentMethodQuery,
  useUpdateBranchPaymentMethodMutation,
  useGetBranchLopQuery,
  useUpdateBranchLopMutation,
  useGetBranchOvertimeQuery,
  useUpdateBranchOvertimeMutation,
  useGetBranchPenaltiesQuery,
  useUpdateBranchPenaltiesMutation,
} = companyBranchConfigsApi;
